import React from "react";
import { useParams } from "react-router-dom";
import DailyMaritimeBriefing from "@/components/daily/DailyMaritimeBriefing";
import { useLanguage } from "@/lib/LanguageContext";
import { getTranslationsForLanguage } from "@/i18n";

const pageHeroCopy = {
  en: {
    eyebrow: "Morning command brief",
    title: "Make the next safe call before you leave the dock.",
    intro: "A focused skipper snapshot for today: local conditions, route confidence, crew safety reminders, and one quick navigation challenge in a single pre-departure view.",
  },
  he: {
    eyebrow: "תדריך פיקוד בוקר",
    title: "קבלו החלטה בטוחה לפני שעוזבים את הרציף.",
    intro: "תמונת מצב ממוקדת לסקיפר: תנאים מקומיים, ביטחון במסלול, תזכורות בטיחות לצוות ואתגר ניווט קצר בתצוגה אחת לפני יציאה.",
  },
  es: {
    eyebrow: "Briefing de mando matinal",
    title: "Toma la decisión segura antes de soltar amarras.",
    intro: "Una vista enfocada para el patrón: condiciones locales, confianza de ruta, recordatorios de seguridad y un reto de navegación rápido antes de zarpar.",
  },
  it: {
    eyebrow: "Briefing comando mattutino",
    title: "Decidi in sicurezza prima di lasciare l’ormeggio.",
    intro: "Uno snapshot per lo skipper: condizioni locali, fiducia sulla rotta, promemoria sicurezza e una breve sfida di navigazione prima della partenza.",
  },
  fr: {
    eyebrow: "Briefing de commandement",
    title: "Prenez la bonne décision avant de quitter le quai.",
    intro: "Un aperçu ciblé pour skipper : conditions locales, confiance de route, rappels sécurité équipage et défi navigation rapide avant le départ.",
  },
  de: {
    eyebrow: "Morgendliches Kommando-Briefing",
    title: "Triff die sichere Entscheidung, bevor du ablegst.",
    intro: "Ein fokussierter Skipper-Überblick: lokale Bedingungen, Routenvertrauen, Sicherheitsnotizen für die Crew und eine kurze Navigationsfrage vor dem Ablegen.",
  },
  pt: {
    eyebrow: "Briefing de comando matinal",
    title: "Tome a decisão segura antes de sair do cais.",
    intro: "Um resumo focado para o skipper: condições locais, confiança da rota, lembretes de segurança e um desafio rápido de navegação antes da partida.",
  },
  ar: {
    eyebrow: "موجز قيادة صباحي",
    title: "اتخذ القرار الآمن قبل مغادرة الرصيف.",
    intro: "لقطة مركزة للربان: الظروف المحلية، ثقة المسار، تذكيرات سلامة الطاقم وتحدي ملاحة سريع قبل الانطلاق.",
  },
  el: {
    eyebrow: "Πρωινή ενημέρωση κυβερνήτη",
    title: "Πάρτε την ασφαλή απόφαση πριν φύγετε από την προβλήτα.",
    intro: "Στοχευμένη εικόνα για skipper: τοπικές συνθήκες, εμπιστοσύνη διαδρομής, υπενθυμίσεις ασφάλειας και σύντομη πρόκληση πλοήγησης πριν την αναχώρηση.",
  },
  ru: {
    eyebrow: "Утренний командный брифинг",
    title: "Примите безопасное решение до отхода от причала.",
    intro: "Сфокусированный обзор для шкипера: местные условия, уверенность маршрута, напоминания экипажу и короткое навигационное задание перед выходом.",
  },
  tr: {
    eyebrow: "Sabah kaptan brifingi",
    title: "Rıhtımdan ayrılmadan güvenli kararı verin.",
    intro: "Kaptan için odaklı özet: yerel koşullar, rota güveni, ekip güvenlik hatırlatmaları ve çıkış öncesi kısa navigasyon sorusu.",
  },
  zh: {
    eyebrow: "晨间船长简报",
    title: "离港前做出更安全的决定。",
    intro: "面向船长的聚焦快照：本地条件、航线信心、船员安全提醒，以及出航前的快速导航挑战。",
  },
};

export default function DailyBriefingPage() {
  const { lang: routeLang } = useParams();
  const { language } = useLanguage();
  const currentLanguage = routeLang || language || "en";
  getTranslationsForLanguage(currentLanguage, "dailyBriefing");
  const hero = pageHeroCopy[currentLanguage];
  const isRTL = currentLanguage === "he" || currentLanguage === "ar";

  if (!hero) return null;

  return (
    <div dir={isRTL ? "rtl" : "ltr"} className="w-full max-w-full overflow-hidden bg-background text-foreground">
      <section className="maritime-container pt-8 md:pt-12">
        <div className="rounded-[2.5rem] border border-primary/20 bg-card p-6 shadow-2xl shadow-ocean/10 md:p-10">
          <p className="text-sm font-black uppercase tracking-[0.22em] text-primary">{hero.eyebrow}</p>
          <h1 className="mt-3 max-w-4xl font-heading text-4xl font-black md:text-6xl">{hero.title}</h1>
          <p className="mt-4 max-w-3xl text-lg leading-8 text-muted-foreground">{hero.intro}</p>
        </div>
      </section>
      <DailyMaritimeBriefing />
    </div>
  );
}